import {
  Box,
  Container,
  Typography,
  useTheme,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Link,
} from "@mui/material";
import { ArrowBackIosNew as ArrowBackIosNewIcon } from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { authClient } from "../../lib/auth-client";
import { useMembershipStatus } from "../../hooks/useMembershipApi";
import { ManagePlan } from "../../components/AccountSettings/ManagePlan";
import NativeManageSubscription from "../../components/AccountSettings/NativeManageSubscription";
import DeleteAccountCard from "../../components/AccountSettings/DeleteAccountCard";
import NotificationPreferencesCard from "../../components/Notifications/NotificationPreferencesCard";
import { getTierByVariantId, isLifetimeVariant } from "../../data/pricingPlans";
import { isNative, openExternal } from "../../lib/platform";
import dayjs from "dayjs";

export default function AccountSettings() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: session } = authClient.useSession();
  const { data: membershipStatus, isLoading: membershipLoading } = useMembershipStatus({
    noCache: true, // Always fetch fresh membership data on this page
  });

  const membership = membershipStatus?.membership;
  const isInGracePeriod = membership?.isInGracePeriod ?? false;
  const isActive = membershipStatus?.hasActiveMembership ?? false;

  const getPlanName = (variantId?: string) => {
    if (isLifetimeVariant(variantId)) return "Lifetime";
    const match = getTierByVariantId(variantId);
    if (!match) return "Pro";
    return `${match.tier.name} · ${match.interval === "monthly" ? "Monthly" : "Annual"}`;
  };

  // Get status badge configuration
  const getStatusBadge = () => {
    if (!isActive) {
      return {
        label: "Expired",
        color: "#ef4444",
        bg: "rgba(239, 68, 68, 0.1)",
        border: "rgba(239, 68, 68, 0.2)",
      };
    }

    if (isInGracePeriod) {
      return {
        label: "Ending Soon",
        color: "#f59e0b",
        bg: "rgba(245, 158, 11, 0.1)",
        border: "rgba(245, 158, 11, 0.2)",
      };
    }

    return {
      label: "Active",
      color: "#22c55e",
      bg: "rgba(34, 197, 94, 0.1)",
      border: "rgba(34, 197, 94, 0.2)",
    };
  };

  const statusBadge = getStatusBadge();

  const handleManageSubscription = () => {
    if (membership?.customerPortalUrl) {
      void openExternal(membership.customerPortalUrl);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        backgroundColor: theme.palette.background.default,
        // Notch/status-bar clearance in the native app (env() is 0 in
        // ordinary browsers) — this page renders outside the tab shell
        pt: "calc(env(safe-area-inset-top, 0px) + 32px)",
        pb: "calc(env(safe-area-inset-bottom, 0px) + 32px)",
        px: 2,
      }}
    >
      <Container maxWidth="lg">
        {/* Logo — hidden in the native app (brand lives in the app chrome) */}
        {!isNative() && (
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Link component={RouterLink} to="/dashboard" sx={{ display: "inline-block" }}>
              <img
                src="/insightLongLogoPrimaryWhiteText.svg"
                alt="Insight Analytics"
                style={{
                  height: 32,
                  width: "auto",
                  maxWidth: "180px",
                }}
              />
            </Link>
          </Box>
        )}

        {/* Page Header — nav-bar pattern: back chevron top-left (navigation
            lives at the top), centred title. Replaces the old bottom
            "Back to Dashboard" button, which stacked against the delete
            action and gave two unrelated controls equal visual weight. */}
        <Box sx={{ position: "relative", mb: 1 }}>
          <IconButton
            onClick={() => navigate("/dashboard")}
            aria-label="Back to dashboard"
            sx={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              width: 36,
              height: 36,
              borderRadius: 2,
              color: theme.palette.text.primary,
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.2)"
                  : "rgba(0, 0, 0, 0.2)"
              }`,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.02)"
                  : "rgba(0, 0, 0, 0.02)",
              "&:hover": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.3)"
                    : "rgba(0, 0, 0, 0.3)",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              textAlign: "center",
              px: 5,
            }}
          >
            Account Settings
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            textAlign: "center",
            mb: 4,
          }}
        >
          Manage your account and subscription
        </Typography>

        {/* Main Content Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
            mb: 3,
          }}
        >
          {/* Account Information Card */}
          <Box
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.02)"
                  : "rgba(255, 255, 255, 0.9)",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)"
              }`,
              borderRadius: 3,
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Account Information
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontSize: "0.6875rem",
                  }}
                >
                  Email
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                >
                  {session?.user?.email ?? "Loading..."}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontSize: "0.6875rem",
                  }}
                >
                  Name
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                >
                  {session?.user?.name ?? "Not provided"}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Subscription Status Card */}
          {membershipLoading && (
            <Box
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.02)"
                    : "rgba(255, 255, 255, 0.9)",
                border: `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)"
                }`,
                borderRadius: 3,
                p: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          {!membershipLoading && membership && (
            <Box
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.02)"
                    : "rgba(255, 255, 255, 0.9)",
                border: `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)"
                }`,
                borderRadius: 3,
                p: 3,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Subscription
                </Typography>

                <Chip
                  label={statusBadge.label}
                  size="small"
                  sx={{
                    backgroundColor: statusBadge.bg,
                    color: statusBadge.color,
                    border: `1px solid ${statusBadge.border}`,
                    fontWeight: 600,
                    fontSize: "0.6875rem",
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontSize: "0.6875rem",
                    }}
                  >
                    Plan
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                  >
                    {getPlanName(membership.variantId)}
                  </Typography>
                </Box>

                {isInGracePeriod && membership.expiresAt && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.6875rem",
                      }}
                    >
                      Ends On
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                    >
                      {dayjs(membership.expiresAt).format("MMMM D, YYYY")}
                    </Typography>
                  </Box>
                )}

                {!isInGracePeriod && isActive && membership.renewsAt && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.6875rem",
                      }}
                    >
                      Renews On
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                    >
                      {dayjs(membership.renewsAt).format("MMMM D, YYYY")}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Manage Subscription Button (Lemon Squeezy portal — web only:
                  the portal surfaces prices/plan changes, which may not be
                  shown inside the native app) */}
              {isActive && membership.customerPortalUrl && !isNative() && (
                <Button
                  onClick={handleManageSubscription}
                  variant="contained"
                  size="small"
                  fullWidth
                  sx={{
                    backgroundColor: "#22c55e",
                    color: "#ffffff",
                    textTransform: "none",
                    fontWeight: 600,
                    py: 0.75,
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "#16a34a",
                    },
                  }}
                >
                  Manage Subscription
                </Button>
              )}

              {/* Renew Button for Expired */}
              {!isActive && (
                <Button
                  onClick={() => navigate("/membership")}
                  variant="contained"
                  size="small"
                  fullWidth
                  sx={{
                    backgroundColor: "#22c55e",
                    color: "#ffffff",
                    textTransform: "none",
                    fontWeight: 600,
                    py: 0.75,
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "#16a34a",
                    },
                  }}
                >
                  Renew Subscription
                </Button>
              )}
            </Box>
          )}

          {/* No Membership Placeholder */}
          {!membershipLoading && !membership && (
            <Box
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.02)"
                    : "rgba(255, 255, 255, 0.9)",
                border: `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)"
                }`,
                borderRadius: 3,
                p: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                No active subscription
              </Typography>
              <Button
                onClick={() => navigate("/membership")}
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#22c55e",
                  color: "#ffffff",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1,
                  px: 2,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#16a34a",
                  },
                }}
              >
                Get Started
              </Button>
            </Box>
          )}
        </Box>

        {/* Manage Plan — in-app tier switching (upgrades pay now, downgrades
            drop usage limits immediately with no credit). Lemon Squeezy (web)
            subscriptions only, and never inside the native app: the LS charge
            flow may not run there (App Store guideline 3.1.1). */}
        {!membershipLoading &&
          membership &&
          isActive &&
          !isInGracePeriod &&
          !isNative() &&
          membership.billingSource !== "revenuecat" && <ManagePlan membership={membership} />}

        {/* Store-billed (RevenueCat) subscriptions are managed through the
            App Store / Google Play subscription sheet instead */}
        {!membershipLoading && membership && isActive && membership.billingSource === "revenuecat" && (
          <NativeManageSubscription membership={membership} />
        )}

        {/* Grace Period Warning */}
        {!membershipLoading && membership && isInGracePeriod && membership.expiresAt && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(245, 158, 11, 0.1)"
                  : "rgba(245, 158, 11, 0.05)",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(245, 158, 11, 0.2)"
                  : "rgba(245, 158, 11, 0.15)"
              }`,
              borderRadius: 2,
              py: 1,
              "& .MuiAlert-icon": {
                color: "#f59e0b",
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.875rem" }}>
              Your subscription will end on{" "}
              {dayjs(membership.expiresAt).format("MMMM D, YYYY")}.{" "}
              {/* Native must not point at the external Lemon Squeezy portal
                  (App Store 3.1.1 steering) — the in-app upgrade screen is
                  the store-safe path back. */}
              {isNative() && "You can resubscribe from the Upgrade screen at any time."}
              {!isNative() && "You can reactivate it in the customer portal."}
            </Typography>
          </Alert>
        )}

        {/* Email / push preferences per notification category. Shown to every
            signed-in user, member or not: a privacy control is never hidden. */}
        {session?.user && <NotificationPreferencesCard />}

        {/* Quiet account-deletion entry point, alone at the end of the
            content (navigation moved to the top-left chevron, so the two no
            longer stack). In-app deletion is required on native by App Store
            guideline 5.1.1(v); always rendered (web + native, any
            subscription state): deletion must never be unreachable. The
            confirm dialog carries the warnings. */}
        {!membershipLoading && <DeleteAccountCard membership={membership ?? null} />}

        {/* Legal footer — Terms/Privacy must be reachable from the signed-in
            app, not just auth pages and the paywall (App Store metadata and
            review both expect in-app access) */}
        <Box sx={{ mt: 3, pb: 2, textAlign: "center" }}>
          <Link
            component={RouterLink}
            to="/terms"
            sx={{
              color: theme.palette.text.secondary,
              textDecoration: "none",
              fontSize: "0.75rem",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Terms of Service
          </Link>
          <Typography
            component="span"
            sx={{ mx: 1.5, color: theme.palette.text.secondary, fontSize: "0.75rem" }}
          >
            •
          </Typography>
          <Link
            component={RouterLink}
            to="/privacy-policy"
            sx={{
              color: theme.palette.text.secondary,
              textDecoration: "none",
              fontSize: "0.75rem",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Privacy Policy
          </Link>
        </Box>
      </Container>
    </Box>
  );
}
